import type { Home } from '@lmc/ts-common'
import { Button, PageHeader } from '@lmc/ui-kit'
import useResident from 'hooks/useResident'
import { useRouter } from 'hooks/useRouter'
import { NAMESPACE } from 'locale'
import { riskAssessmentReportConstants as translations } from 'locale/riskAssessmentReports'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import PerAssessmentScreen from './screens/PerAssessmentScreen/PerAssessmentScreen'
import PerResidentScreen from './screens/PerResidentScreen/PerResidentScreen'

const isDesktop = isBrowser || isTablet

export type RiskAssessmentDashboardType = 'resident' | 'riskAssessment'

type RiskAssessmentDashboardProps = {
  type: RiskAssessmentDashboardType
}

const reportsUrl = '/reports/care-planning/risk-assessments'

const RiskAssessmentDashboard = ({ type }: RiskAssessmentDashboardProps) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENT_REPORT)
  const navigate = useNavigate()
  const {
    params: { id }
  } = useRouter()

  const { resident, isLoading } = useResident({ id })

  const pageHeaderTitle =
    isLoading || !resident
      ? t(translations.RISK_ASSESSMENT_SCORES)
      : `${resident?.name?.first} ${resident?.name?.last}'s ${t(
          translations.RISK_ASSESSMENT_SCORES
        )}`

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  return (
    <>
      <PageHeader
        sticky
        title={<TitleContainer>{pageHeaderTitle}</TitleContainer>}
        // This is a reused code snippet for the back button that's inside a header that's already inside the codebase. Might be a good idea to refactor it at some point
        onBack={
          !isDesktop
            ? () => navigate(`/residents/${id}/profile`, { replace: true })
            : undefined
        }
      >
        {type === 'resident' && (
          <Button variant="text">
            <Link to={reportsUrl}>
              {t(translations.VIEW_TEXT, {
                residents: preferredPeopleNominative
              })}
            </Link>
          </Button>
        )}
      </PageHeader>
      <Wrapper type={type}>
        {type === 'riskAssessment' ? (
          <PerAssessmentScreen />
        ) : (
          <PerResidentScreen />
        )}
      </Wrapper>
    </>
  )
}

const Wrapper = styled.div<{ type: RiskAssessmentDashboardType }>`
  max-width: 1480px;
  padding: 0 0 50px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin: ${(props) => (props?.type === 'resident' ? '0' : '0 24px')};
  }
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

export default RiskAssessmentDashboard
